import { useQueryClient } from '@tanstack/react-query';

import type { TypeCheckInOutStatus, TypeCodeState, TypeWorkInfo } from '../../types';
import { registerMockAPI } from '~/mocks/mockApis';
import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';
import { useTokenStore } from '~/store';

/** @category useGetCommuteInfo */
export type TypeGetCommuteInfoResponse = {
  code: string;
  message: string;
  data: {
    /**
     * 출근 상태
     * @remarks
     * - START_WORK(공통 - 출근중)
     * - AVAILABLE_LABOR_CONTRACT (일용직 - 계약체결 필요)
     * - AVAILABLE_START_WORK (공통 - 출근 가능)
     * - UNAVAILABLE_START_WORK_BY_INSUFFICIENT_REST (공통 - 조퇴 후 10시간 미만으로 출근 불가능)
     * - UNAVAILABLE_START_WORK_BY_MISSING_WORK_PLAN (상용직 - 근무계획이 등록되지 않았거나, 조출(5시간) 불가 시각이라 출근 불가능)
     * - UNAVAILABLE_START_WORK_BY_DAY_OFF_WORK_PLAN (상용직 - 휴무 근무유형 근무계획으로 출근 불가능)
     * - UNAVAILABLE_START_WORK_BY_TODAY_WORK_PLAN_ENDED (상용직 - 다음 근무계획이 근무시작가능 범위 밖에 있어 출근 불가능)
     * - UNAVAILABLE_START_WORK_BY_TODAY_WORK_END (일용직 - 금일(오늘) 퇴근하여 출근 불가능)
     */
    status: TypeCodeState;
    data: Omit<TypeWorkInfo, 'status' | 'recentCheckInOut' | 'workType'> | null;
    /** 최근 체크인/체크아웃 정보 */
    recentCheckInOut: TypeCheckInOutStatus | null;
    /** 근무 유형 */
    workType: TypeCodeState | null;
  };
};

/**
 * @description 작업자의 현재 출근 및 체크인 정보를 가져오는 api 요청에 사용되는 커스텀 훅
 */
export const useGetCommuteInfo = () => {
  const queryClient = useQueryClient();

  const requestGetCommuteInfo = async ({
    onSuccess,
    onError,
    showLoading = true,
  }: {
    onSuccess: (response: TypeGetCommuteInfoResponse) => void;
    onError?: (error: TypeErrorFormat) => void;
    showLoading?: boolean;
  }) => {
    const endpoint = '/commutes/current';
    const query = queryClient.ensureQueryData({
      queryKey: ['commutes', 'current'],
      queryFn: () => axiosInstance.get(endpoint),
      gcTime: 0,
    });

    return requestHelper(() => query, onSuccess, onError, undefined, { showLoading });
  };

  return requestGetCommuteInfo;
};

registerMockAPI<TypeGetCommuteInfoResponse>({
  method: 'get',
  endpoint: '/commutes/current',
  label: '근무 기본 정보/현재 출근 정보 조회',
  scenarios: [
    {
      name: '200 / 일용직',
      response: {
        code: '0000',
        message: '성공',
        data: {
          workType: null,
          workDate: '2026-06-25',
          status: { code: 'AVAILABLE_LABOR_CONTRACT', text: '근로계약가능' },
          data: null,
          recentCheckInOut: null,
        },
      } as unknown as TypeGetCommuteInfoResponse,
    },
    {
      name: '200 / 상용직',
      response: {
        code: '0000',
        message: '성공',
        data: {
          workType: { code: '근무', text: '근무' },
          workDate: '2025-03-25',
          status: { code: 'START_WORK', text: '출근' },
          data: {
            cluster: { code: 'CC03', text: '평택' },
            center: { code: 'GPH1', text: '평택상온' },
            workPart: { code: 'OB', text: 'OB' },
            teamName: '상용직',
            workShift: '12:00 ~ 01:00',
            overWorkWishedStatus: { code: 'NOT_WISHED', text: '연장 근무 비희망' },
            checkInOutStatus: { code: 'CHECK_OUT', text: '공정 체크아웃' },
            endWorkAvailableFrom: '2025-03-26T00:30:00',
            qrCode: 'kurlyro12',
          },
          recentCheckInOut: null,
        },
      } as unknown as TypeGetCommuteInfoResponse,
    },
    {
      name: '401 / 인증 실패',
      status: 401,
      preCondition: {
        action: () => undefined,
        cleanUp: () => {
          useTokenStore.getState().actions.setRefreshToken('');
        }
      },
      response: {
        code: 'AUTH_MEMBER',
        message: '인증에 실패했습니다.',
        data: null,
      } as unknown as TypeGetCommuteInfoResponse,
    },
    {
      name: '500 / 서버 에러',
      status: 500,
      response: {
        code: '500',
        message: '서버 내부 오류가 발생했습니다.',
        data: null,
      } as unknown as TypeGetCommuteInfoResponse,
    },
    {
      name: '타임아웃',
      status: undefined,
      delay: 11000,
      response: {
        code: 'ECONNABORTED',
        message: '요청 시간이 초과되었어요. 다시 시도해주세요.',
        data: null,
      } as unknown as TypeGetCommuteInfoResponse,
    },
  ],
});
